import * as React from "react";
import { useAnalyticsConfiguration } from "../../providers/analytics-configuration-provider";

/**
 * Triple Whale Pixel binding for the React islands (BBNT_TripleWhaleEvents,
 * docs/tw-events-plan.md §4.2). The dedup, the funnel state machine and the identity
 * transport client live in the Magento module's RequireJS modules and are loaded on
 * first use through the page's RequireJS, so Luma and the islands share one
 * implementation and one `localStorage` state.
 *
 * Every call is a no-op while the configuration is not loaded, the Pixel is disabled
 * for the store view or the backend contract version is not the one this binding was
 * built against.
 */

/** The Pixel contract version this binding is built against (module Config::SUPPORTED_CONTRACT_VERSION). */
export const TRIPLEWHALE_SUPPORTED_CONTRACT_VERSION = 1;

export type TripleWhaleFunnelStep =
  | "checkoutStarted"
  | "shippingSubmitted"
  | "paymentSubmitted";

export type TripleWhaleFunnelOutcome =
  | "fired"
  | "duplicate"
  | "pending"
  | "ignored";

export interface TripleWhaleCartIdentity {
  contractVersion: number;
  shop: string;
  cartKey: string;
  previousCartKey: string | null;
  lineItems: Array<{ i: string; q: number; v: string }>;
  items: Array<{ i: string; q: number; v: string }>;
}

interface TripleWhaleModules {
  events: {
    fire: (name: string, payload: object, eventId?: string) => boolean;
    fireCustom: (name: string, properties: object, eventId?: string) => boolean;
  };
  funnel: {
    submit: (
      step: TripleWhaleFunnelStep,
      identity: TripleWhaleCartIdentity,
      options?: { methodCode?: string; email?: string | null }
    ) => TripleWhaleFunnelOutcome;
    contact: (
      email: string,
      identity: TripleWhaleCartIdentity,
      options?: { replayPending?: boolean }
    ) => string[];
  };
  cartIdentity: {
    fetchIdentity: (
      url: string,
      items?: Array<{ product_id: string; qty: number }>
    ) => Promise<TripleWhaleCartIdentity>;
  };
}

const MODULE_IDS = [
  "BBNT_TripleWhaleEvents/js/tw-events",
  "BBNT_TripleWhaleEvents/js/tw-funnel",
  "BBNT_TripleWhaleEvents/js/tw-cart-identity",
];

export interface UseTripleWhaleConsumerInterface {
  isConfigurationLoaded: boolean;
  isEnabled: boolean;
  /** The store view's display currency (ISO 4217), '' when unknown. */
  currency: string;
  fire: (name: string, payload: object, eventId?: string) => Promise<boolean>;
  fireCustom: (
    name: string,
    properties: object,
    eventId?: string
  ) => Promise<boolean>;
  identify: (
    items?: Array<{ product_id: string; qty: number }>
  ) => Promise<TripleWhaleCartIdentity | null>;
  submitFunnelStep: (
    step: TripleWhaleFunnelStep,
    options?: { methodCode?: string; email?: string | null }
  ) => Promise<TripleWhaleFunnelOutcome>;
  captureContact: (email: string) => Promise<string[]>;
  consumerInitializationGuard: <AnalyticsInterface extends unknown>(props: {
    initializedInterface: AnalyticsInterface;
  }) => AnalyticsInterface;
}

export const useTripleWhale = (): UseTripleWhaleConsumerInterface => {
  const { isConfigurationLoaded, analyticsConfiguration } =
    useAnalyticsConfiguration();
  const triplewhale = analyticsConfiguration?.triplewhale;
  const shop = triplewhale?.shop || "";
  const cartIdentityUrl = triplewhale?.cartIdentityUrl || "";
  const currency = triplewhale?.currency || "";
  const isEnabled = !!(
    triplewhale?.enabled &&
    triplewhale.contractVersion === TRIPLEWHALE_SUPPORTED_CONTRACT_VERSION &&
    shop &&
    cartIdentityUrl
  );
  const customEventsEnabled = !!triplewhale?.customEventsEnabled;
  const funnelEventsEnabled = !!triplewhale?.funnelEventsEnabled;

  const modulesRef = React.useRef<Promise<TripleWhaleModules> | null>(null);
  const loadModules = React.useCallback((): Promise<TripleWhaleModules> => {
    if (!modulesRef.current) {
      modulesRef.current = new Promise((resolve, reject) => {
        const requirejs = (window as any).require;
        if (typeof requirejs !== "function") {
          reject(new Error("RequireJS is not available on this page."));
          return;
        }
        requirejs(
          MODULE_IDS,
          (
            events: TripleWhaleModules["events"],
            funnel: TripleWhaleModules["funnel"],
            cartIdentity: TripleWhaleModules["cartIdentity"]
          ) => resolve({ events, funnel, cartIdentity }),
          (error: unknown) => {
            modulesRef.current = null;
            reject(error);
          }
        );
      });
    }
    return modulesRef.current;
  }, []);

  const fire: UseTripleWhaleConsumerInterface["fire"] = React.useCallback(
    (name, payload, eventId) => {
      if (!isEnabled) {
        return Promise.resolve(false);
      }
      return loadModules()
        .then((modules) => modules.events.fire(name, payload, eventId))
        .catch(() => false);
    },
    [isEnabled, loadModules]
  );

  const fireCustom: UseTripleWhaleConsumerInterface["fireCustom"] =
    React.useCallback(
      (name, properties, eventId) => {
        if (!isEnabled || !customEventsEnabled) {
          return Promise.resolve(false);
        }
        return loadModules()
          .then((modules) => modules.events.fireCustom(name, properties, eventId))
          .catch(() => false);
      },
      [isEnabled, customEventsEnabled, loadModules]
    );

  const identify: UseTripleWhaleConsumerInterface["identify"] =
    React.useCallback(
      (items) => {
        if (!isEnabled) {
          return Promise.resolve(null);
        }
        return loadModules()
          .then((modules) =>
            modules.cartIdentity.fetchIdentity(cartIdentityUrl, items)
          )
          .catch(() => null);
      },
      [isEnabled, cartIdentityUrl, loadModules]
    );

  const submitFunnelStep: UseTripleWhaleConsumerInterface["submitFunnelStep"] =
    React.useCallback(
      (step, options) => {
        if (!isEnabled || !funnelEventsEnabled) {
          return Promise.resolve("ignored");
        }
        return Promise.all([loadModules(), identify()])
          .then(([modules, identity]) =>
            identity ? modules.funnel.submit(step, identity, options) : "ignored"
          )
          .catch((): TripleWhaleFunnelOutcome => "ignored");
      },
      [isEnabled, funnelEventsEnabled, loadModules, identify]
    );

  const captureContact: UseTripleWhaleConsumerInterface["captureContact"] =
    React.useCallback(
      (email) => {
        if (!isEnabled || !email) {
          return Promise.resolve([]);
        }
        // Contact itself is a built-in event and stays allowed; the replay of pending
        // funnel steps it triggers obeys the funnel switch.
        return Promise.all([loadModules(), identify()])
          .then(([modules, identity]) =>
            identity
              ? modules.funnel.contact(email, identity, {
                  replayPending: funnelEventsEnabled,
                })
              : []
          )
          .catch((): string[] => []);
      },
      [isEnabled, funnelEventsEnabled, loadModules, identify]
    );

  const consumerInitializationGuard: UseTripleWhaleConsumerInterface["consumerInitializationGuard"] =
    React.useCallback(
      <AnalyticsInterface extends unknown>(props: {
        initializedInterface: AnalyticsInterface;
      }): AnalyticsInterface => {
        return React.useMemo(
          () =>
            ({
              ...(isConfigurationLoaded
                ? (props.initializedInterface as { [key: string]: any })
                : {}),
            } as AnalyticsInterface),
          [isConfigurationLoaded, props.initializedInterface]
        );
      },
      [isConfigurationLoaded]
    );

  return React.useMemo(
    () => ({
      isConfigurationLoaded,
      isEnabled,
      currency,
      fire,
      fireCustom,
      identify,
      submitFunnelStep,
      captureContact,
      consumerInitializationGuard,
    }),
    [
      isConfigurationLoaded,
      isEnabled,
      currency,
      fire,
      fireCustom,
      identify,
      submitFunnelStep,
      captureContact,
      consumerInitializationGuard,
    ]
  );
};
